import { ActualItem, GetJobWorkareaItemsResponse, ScheduledEvent, Workarea } from "../scheduleApi"
import { ItemClasses, PricingTypes, ProjectTypes } from "../../../common/constants"
import { sum } from "../../../helpers/array"

export const SECONDS_IN_HOUR = 3600
export type Duration = { seconds: number }

export const getJobEstimatedDuration = (workareas: Workarea[], jobItems: GetJobWorkareaItemsResponse | undefined): Duration => {
  if (!jobItems) {
    return { seconds: 0 }
  }

  const approvedWorkareas = workareas.filter(x => !x.isOnHold && x.isApproved)

  const seconds = sum(approvedWorkareas, workarea => {
    const occurrences = workarea.pricingType === PricingTypes.PerVisit ? workarea.estimatedOccurrences ?? 1 : 1
    const laborItems = jobItems.estimatedItems.filter(x => x.class === ItemClasses.Labor && x.workareaId === workarea.id)
    return occurrences * sum(laborItems, x => x.quantity * SECONDS_IN_HOUR)
  })

  return { seconds }
}

export const getJobTotalActualDuration = (workareas: Workarea[], actualItems: ActualItem[]): Duration => {
  const approvedWorkareaIds = workareas.filter(x => !x.isOnHold && x.isApproved).map(x => x.id)
  const laborItems = actualItems.filter(x => x.class === ItemClasses.Labor && approvedWorkareaIds.includes(x.workareaId))
  return { seconds: sum(laborItems, x => x.quantity * SECONDS_IN_HOUR) }
}

export const getRemainingHoursData = (completedJobTime: Duration, estimatedJobTime: Duration) => {
  const totalEstimatedSeconds = estimatedJobTime.seconds
  const totalCompletedSeconds = completedJobTime.seconds

  if (totalEstimatedSeconds === 0) {
    return {
      remainingSeconds: -totalCompletedSeconds,
      remainingSecondsPercentage: -100,
      jobCompletionPercentage: 0
    }
  }

  const remainingSeconds = totalEstimatedSeconds - totalCompletedSeconds
  const remainingSecondsPercentage = (remainingSeconds / totalEstimatedSeconds) * 100
  const jobCompletionPercentage = (totalCompletedSeconds / totalEstimatedSeconds) * 100

  return {
    remainingSeconds,
    remainingSecondsPercentage,
    jobCompletionPercentage
  }
}

export const formatDuration = ({ seconds: totalSeconds }: Duration): string => {
  if (totalSeconds === 0) {
    return '0 h'
  }

  const hours = Math.floor(Math.abs(totalSeconds) / SECONDS_IN_HOUR)
  const minutes = Math.floor((Math.abs(totalSeconds) - hours * SECONDS_IN_HOUR) / 60)
  const seconds = Math.abs(totalSeconds) % 60

  const parts: [number, string][] = []
  if (hours > 0) {
    parts.push([hours, 'h'])
    parts.push([minutes, 'm'])
  } else if (minutes > 0) {
    parts.push([minutes, 'm'])
    parts.push([seconds, 's'])
  } else if (seconds > 0) {
    parts.push([seconds, 's'])
  }

  return `${totalSeconds < 0 ? '-' : ''}${parts.map(([value, unit]) => `${value}${unit}`).join(' ')}`
}

export const getProjectUrl = (event: ScheduledEvent): string => {
  const slug = event.jobId
  switch (event.projectType) {
    case ProjectTypes.Project:
      return event.projectSold ? `projects/${slug}` : `project-estimate/${slug}`
    case ProjectTypes.Ticket:
      return event.projectSold ? `tickets/${slug}` : `ticket-estimate/${slug}`
    case ProjectTypes.Maintenance:
      return event.projectSold ? `maintenance/${slug}` : `maintenance-estimate/${slug}`
    default:
      return "unknown"
  }
}

export const handleViewJobDetails = (event: ScheduledEvent) => {
  const url = getProjectUrl(event)

  if (url && url !== 'unknown') {
    window.open(`#!/${url}`, '_blank')
  }
}